import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import dynamic from "next/dynamic";

import { useTranslation } from "next-i18next";
import { GenericObject } from "@vahak/core/dist/custom-types";
import { prepareMobileSheetOpen } from "@vahak/core-ui/dist/methods";

import { loadI18nNamespaces } from "../../../hooks/useLoadI18nNamespaces";
import { useLoadAddDeepLink } from "../../../hooks/useLoadAddDeepLink";
import { WEB_V2_POSTING_LOCALIZATION_NAMESPACES } from "../../../methods/getRouteLocalizationNamespaces";
import { LoadQuoteValues } from "../load-quote";
import { CompactLoadPostingInitialValues, OpenCompactPostingOptions } from "./types";
import { IEventTrackerProp } from "../../../types/events";

export type { OpenCompactPostingOptions } from "./types";

const CompactLoadPostingModal = dynamic(() => import("./CompactLoadPostingModal"), {
    ssr: false
});

interface CompactLoadPostingContextValue {
    isCompactPostingOpen: boolean;
    /** True while waiting for iOS zoom reset / i18n before the sheet mounts. */
    isCompactPostingOpening: boolean;
    openCompactPosting: (
        values?: CompactLoadPostingInitialValues | LoadQuoteValues,
        options?: OpenCompactPostingOptions
    ) => void;
    closeCompactPosting: () => void;
}

const CompactLoadPostingContext = createContext<CompactLoadPostingContextValue | null>(null);

/** Must render inside Provider. */
function CompactLoadPostingDeepLinkEffects() {
    useLoadAddDeepLink();
    return null;
}

function buildSessionKey(values?: CompactLoadPostingInitialValues | LoadQuoteValues) {
    if (!values) {
        return "empty";
    }

    return [
        values.source?.place_id,
        values.destination?.place_id,
        values.lorryType,
        values.tonnes,
        "loadId" in values ? values.loadId : undefined
    ].join("-");
}

export function CompactLoadPostingProvider({
    children,
    trackEvent,
    eventData
}: React.PropsWithChildren<IEventTrackerProp>) {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isOpening, setIsOpening] = useState(false);
    const [initialValues, setInitialValues] = useState<CompactLoadPostingInitialValues | undefined>();
    const [sessionEventData, setSessionEventData] = useState<GenericObject | undefined>();
    const [isRepostSession, setIsRepostSession] = useState(false);
    const [sessionOnSuccess, setSessionOnSuccess] = useState<(() => void) | undefined>();

    const [sessionKey, setSessionKey] = useState("closed");

    const openCompactPosting = useCallback(
        (values?: CompactLoadPostingInitialValues | LoadQuoteValues, options?: OpenCompactPostingOptions) => {
            setIsOpening(true);

            void loadI18nNamespaces(i18n, WEB_V2_POSTING_LOCALIZATION_NAMESPACES)
                .then(async () => {
                    await prepareMobileSheetOpen();

                    setInitialValues(values);
                    setSessionEventData(options?.eventData);
                    setIsRepostSession(Boolean(options?.isRepost));
                    // Callback state must be set via updater wrapper, otherwise React treats
                    // the function as a state updater and executes it immediately.
                    setSessionOnSuccess(() => options?.onSuccess);
                    setSessionKey(buildSessionKey(values));
                    setIsOpen(true);
                })
                .finally(() => {
                    setIsOpening(false);
                });
        },
        [i18n]
    );

    const closeCompactPosting = useCallback(() => {
        setIsOpen(false);
    }, []);

    const value = useMemo(
        () => ({
            isCompactPostingOpen: isOpen,
            isCompactPostingOpening: isOpening,
            openCompactPosting,
            closeCompactPosting
        }),
        [closeCompactPosting, isOpen, isOpening, openCompactPosting]
    );

    const mergedEventData = useMemo(() => ({ ...eventData, ...sessionEventData }), [eventData, sessionEventData]);

    return (
        <CompactLoadPostingContext.Provider value={value}>
            {children}
            <CompactLoadPostingDeepLinkEffects />
            {isOpen ? (
                <CompactLoadPostingModal
                    key={sessionKey}
                    isOpen={isOpen}
                    initialValues={initialValues}
                    isRepost={isRepostSession}
                    onSuccess={sessionOnSuccess}
                    onClose={closeCompactPosting}
                    trackEvent={trackEvent}
                    eventData={mergedEventData}
                />
            ) : null}
        </CompactLoadPostingContext.Provider>
    );
}

export function useCompactLoadPosting() {
    const context = useContext(CompactLoadPostingContext);

    if (!context) {
        throw new Error("useCompactLoadPosting must be used within CompactLoadPostingProvider");
    }

    return context;
}

/**
 * Non-throwing variant. Returns `null` when no `CompactLoadPostingProvider` is mounted
 * (e.g. the admin console, which only uses the legacy CS posting flow). Use this in
 * shared entry points that may render outside the consumer app's provider tree.
 */
export function useCompactLoadPostingOptional() {
    return useContext(CompactLoadPostingContext);
}
